import RFB from 'novnc-core';
import {formatDisconnectTimeout} from '../utility.js';

export default (apiUrl) => ({
    sessionID: null,
    wssUrl: null,
    noVncUrl: null,
    startedAt: null,
    runningTime: 0,
    maxTime: 60 * 60,
    loading: true,
    isRunning: false,
    stopping: false,
    zoomed: false,
    connectionRetries: 0,
    maxRetries: 3,
    apiUrl,
    copiedSessionID: false,
    copiedWssUrl: false,
    connectionFailed: false,
    vncAccess: 'password',
    vncPassword: '',
    proxy: null,
    allowedIp: [],
    clientIp: null,
    browserWebsocketAccess: null,
    disconnectTimeoutSeconds: 60,
    hadStableConnection: false,
    hardware: null,

    initSession(session) {
        this.sessionID = session.sessionID;
        this.wssUrl = session.wssUrl;
        this.noVncUrl = session.noVncUrl;
        this.startedAt = session.startedAt;
        this.vncAccess = session.vncAccess || 'password';
        this.vncPassword = session.vncPassword || '';
        this.proxy = session.proxy || null;
        this.allowedIp = session.allowedIp || [];
        this.clientIp = session.clientIp || null;
        this.browserWebsocketAccess = session.browserWebsocketAccess || null;
        this.disconnectTimeoutSeconds = session.disconnectTimeoutSeconds || 60;
        this.hardware = session.hardware || null;

        // Calculate initial running time from startedAt (Unix timestamp)
        const now = Math.floor(Date.now() / 1000);
        this.runningTime = now - this.startedAt;

        // Start timer - always create new one for each component instance
        const timerInterval = setInterval(() => {
            this.runningTime++;
            if (this.runningTime >= this.maxTime) {
                clearInterval(timerInterval);
                setTimeout(() => {
                    this.$dispatch('session-stopped', {sessionID: this.sessionID});
                }, 5000);
            }
        }, 1000);

        // Store cleanup reference
        this._timer = timerInterval;

        // Watch for zoom changes
        this.$watch('zoomed', (zoomed) => this.handleZoom(zoomed));

        // Check if we have a persisted canvas for this session
        const persistentContainer = document.getElementById('vnc-persistent-container');
        const persistedCanvas = persistentContainer?.querySelector(`[data-session-id="${this.sessionID}"]`);

        if (this.hardware === 'real') {
            this.loading = false;
            this.isRunning = true;
        } else if (persistedCanvas && this.$refs.vncCanvas) {
            // Move canvas back from persistent container
            this.$refs.vncCanvas.appendChild(persistedCanvas);
            this.loading = false;
            this.isRunning = true;

            // Trigger resize
            const rfb = this.$store.sessions.getRfb(this.sessionID);
            if (rfb) {
                setTimeout(() => {
                    rfb.scaleViewport = true;
                }, 100);
            }
        } else if (!this.$store.sessions.getRfb(this.sessionID)) {
            // No existing connection, create new one
            this.connectVnc();
        }
    },

    handleZoom(zoomed) {
        const rfb = this.$store.sessions.getRfb(this.sessionID);
        if (!rfb) return;

        if (zoomed) {
            const vncScreen = this.$refs.vncCanvas?.firstElementChild;
            if (!vncScreen) return;

            if (this.$refs.vncCanvasZoomed) {
                this.$refs.vncCanvasZoomed.appendChild(vncScreen);
                requestAnimationFrame(() => {
                    rfb.scaleViewport = true;
                });
            }
        } else {
            const vncScreen = this.$refs.vncCanvasZoomed?.firstElementChild;
            if (!vncScreen) return;

            this.$refs.vncCanvas.appendChild(vncScreen);
            requestAnimationFrame(() => {
                rfb.scaleViewport = true;
            });
        }
    },

    destroy() {
        // Cleanup timer
        if (this._timer) {
            clearInterval(this._timer);
        }

        // Move canvas to persistent container instead of destroying
        const persistentContainer = document.getElementById('vnc-persistent-container');
        if (persistentContainer) {
            // Canvas might be in vncCanvas or vncCanvasZoomed
            const vncScreen = this.$refs.vncCanvas?.firstElementChild || this.$refs.vncCanvasZoomed?.firstElementChild;
            if (vncScreen) {
                vncScreen.setAttribute('data-session-id', this.sessionID);
                persistentContainer.appendChild(vncScreen);
            }
        }
        this.zoomed = false;
    },

    formatTime(seconds) {
        if (seconds < 0) seconds = 0;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return mins + ':' + secs.toString().padStart(2, '0');
    },

    formatDisconnectTimeout,

    connectVnc() {
        if (!this.noVncUrl || !this.$refs.vncCanvas) {
            console.error('Missing vnc url or element');
            this.loading = false;
            return;
        }

        // noVncUrl is https, convert to wss for websockify
        const wsUrl = this.noVncUrl.replace('https://', 'wss://') + '/websockify';
        console.log('Connecting to VNC:', wsUrl);

        const rfb = new RFB(this.$refs.vncCanvas, wsUrl, {
            credentials: {password: this.vncPassword},
            scaleViewport: true,
            resizeSession: false
        });

        rfb.viewOnly = true;
        rfb.clipViewport = true;
        rfb.qualityLevel = 0;
        rfb.compressionLevel = 9;

        // Store RFB instance
        this.$store.sessions.setRfb(this.sessionID, rfb);

        rfb.addEventListener('connect', () => {
            console.log('VNC connected');
            this.connectionRetries = 0;
            this.connectionFailed = false;
            this.loading = false;
            this.isRunning = true;

            setTimeout(() => {
                rfb.scaleViewport = true;
            }, 100);

            // Mark as stable connection after threshold
            setTimeout(() => {
                this.hadStableConnection = true;
                console.log("Stable VNC connection");
            }, 3000);
        });

        rfb.addEventListener('disconnect', (e) => {
            console.log('VNC disconnected', e.detail);

            // Check if clean disconnect (manual stop)
            if (e.detail?.clean === true) {
                this.loading = false;
                this.isRunning = false;

                // If we had a stable connection and now disconnected, refresh dashboard
                if (this.hadStableConnection && window.location.pathname === '/dashboard') {
                    console.log('Stable VNC connection lost. Refreshing dashboard...');
                    this.refreshDashboard();
                    return;
                }

                return;
            }

            if (this.connectionRetries < this.maxRetries) {
                this.connectionRetries++;
                console.log(`Retrying connection (${this.connectionRetries}/${this.maxRetries}) in 1 second...`);
                setTimeout(() => this.connectVnc(), 1000);
            } else {
                this.loading = false;
                this.connectionFailed = true;
            }
        });

        rfb.addEventListener('securityfailure', (e) => {
            console.error('VNC security failure', e.detail);
            this.loading = false;
            this.connectionFailed = true;
        });
    },

    async stopBrowser(apiKey) {
        this.stopping = true;

        const params = new URLSearchParams({
            sessionID: this.sessionID
        });

        try {
            const response = await fetch(`https://${this.apiUrl}/browser/stop`, {
                method: 'POST',
                headers: {'Content-Type': 'application/x-www-form-urlencoded', 'x-cdt-api-key': apiKey},
                body: params
            });

            const json = await response.json();

            this.isRunning = false;
            this.stopping = false;

            if (json.success) {
                // Remove session - store will cleanup RFB and timer
                this.$dispatch('session-stopped', {sessionID: this.sessionID});
            }
        } catch (error) {
            console.error('Error stopping browser:', error);
            this.stopping = false;
        }
    },

    refreshDashboard() {
        window.dispatchEvent(new CustomEvent('goto', {
            detail: {url: '/dashboard'}
        }));
    }
});
